import React, { useRef, useState } from "react";
import type { CampaignReportResponse } from "../../types";
import { exportElementAsPng } from "../../utils/exportUtils";
import {
  CompactLayoutHeader,
  PerformancePulseSlide,
  PortfolioStrengthSlide,
} from "./CompactLayoutSlides";
import { buildCompactLayoutViewModel } from "./compactLayoutData";

interface CompactLayoutProps {
  data: CampaignReportResponse;
  onNewUpload: () => void;
}

export const CompactLayout: React.FC<CompactLayoutProps> = ({ data }) => {
  const [exportingSlide, setExportingSlide] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const slide1Ref = useRef<HTMLDivElement>(null);
  const slide2Ref = useRef<HTMLDivElement>(null);
  const viewModel = buildCompactLayoutViewModel(data);

  const exportSlide = async (
    slideRef: React.RefObject<HTMLDivElement | null>,
    slideName: string,
  ): Promise<boolean> => {
    if (!slideRef.current) {
      setExportError(`Could not export ${slideName}: slide content is unavailable.`);
      return false;
    }

    setExportingSlide(slideName);
    setExportError(null);
    try {
      await exportElementAsPng(
        slideRef.current,
        `${slideName}-${new Date().toISOString().slice(0, 10)}.png`,
        {
          quality: 1.0,
          pixelRatio: 3,
          width: 1920,
          height: 1080,
          backgroundColor: "#ffffff",
          cacheBust: true,
          style: {
            transform: "scale(1)",
            transformOrigin: "top left",
          },
          filter: (node) =>
            !["no-export"].some((className) =>
              node.classList?.contains(className),
            ),
        },
      );
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown export error";
      setExportError(errorMessage);
      return false;
    } finally {
      setExportingSlide(null);
    }
  };

  const exportAllSlides = async () => {
    const firstSlideExported = await exportSlide(slide1Ref, "performance-pulse");
    if (!firstSlideExported) {
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 500));
    await exportSlide(slide2Ref, "portfolio-strength");
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900 py-8 px-6">
      <CompactLayoutHeader
        campaignCount={data.summary.campaign_count}
        exportingSlide={exportingSlide}
        onExportAllSlides={exportAllSlides}
        totalDays={data.summary.total_days}
      />
      {exportError && (
        <div
          className="max-w-[1920px] mx-auto mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          role="alert"
        >
          {exportError}
        </div>
      )}

      <div className="max-w-[1920px] mx-auto space-y-8">
        <PerformancePulseSlide
          onExportSlide={exportSlide}
          reportType={data.report_type}
          slideRef={slide1Ref}
          summary={data.summary}
          viewModel={viewModel}
        />
        <PortfolioStrengthSlide
          onExportSlide={exportSlide}
          reportType={data.report_type}
          slideRef={slide2Ref}
          summary={data.summary}
          viewModel={viewModel}
        />
      </div>
    </div>
  );
};
